// El periodo que se le liquida a cada finca.
//
// No es el mismo para todas: hay fincas que cobran el sábado y otras el 15 y el
// 30. La relación tiene que abrir en EL periodo de esa finca — si abre siempre
// en el mes, quien liquida por semana tiene que corregir las dos fechas cada
// vez, y el día que se le olvide le paga de más.
//
// Todo se calcula en UTC. Sumando días sobre una fecha local, el cambio de hora
// repite o salta un día, y un periodo con un día de más o de menos es plata.

const utc = (f) => new Date(String(f).slice(0, 10) + 'T00:00:00Z');
const ymd = (d) => d.toISOString().slice(0, 10);
const mas = (d, n) => { const x = new Date(d); x.setUTCDate(x.getUTCDate() + n); return x; };

// El último día de ese mes. El 0 del mes siguiente es el último del actual.
const ultimoDelMes = (d) =>
  new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 0)).getUTCDate();

// Qué día empieza la semana de pago. NO es siempre el lunes.
//
// La factura de este cliente va del 15 al 21 de agosto de 2026: sábado a
// viernes. Se cierra el viernes porque es el día que sale a pagar. Otro
// acopiador cierra el domingo y otro el miércoles — es un acuerdo con cada
// finca, no una convención del calendario.
//
// Calcularlo siempre de lunes a domingo partía su semana en dos y la relación
// le mostraba días de la semana anterior mezclados con los de esta.
export const DIAS = ['domingo', 'lunes', 'martes', 'miércoles', 'jueves',
                     'viernes', 'sábado'];

// El lunes, que es lo que hacía antes y sigue siendo lo más común.
export const EMPIEZA_POR_DEFECTO = 1;

// Qué día del mes empieza la quincena. TAMPOCO es siempre el 1.
//
// «No vi la opción que le pedí de poder escoger qué día inicia la quincena.
// Algo así como le puse a las semanales.»
//
// Estaba fijo en el 1 y el 16, que es lo más común aquí pero no lo único: hay
// acuerdos que cortan el 5 y el 20, o el 6 y el 21. Con la quincena fija, esa
// finca veía su relación partida en dos y con días de la quincena anterior
// mezclados — el mismo problema que ya tenía la semana.
//
// Se escoge UN día y el otro sale solo, quince días después: quien paga el 5 y
// el 20 no tiene dos acuerdos, tiene uno. Pedirle los dos días sería dejar que
// se puedan poner dos que no partan el mes.
export const QUINCENA_POR_DEFECTO = 1;

// Del 1 al 15. Más allá del 15 el segundo corte se sale de febrero, y una
// quincena que en febrero cae en otro día no es una quincena: es un lío.
// Además no hace falta — empezar el 20 es el mismo acuerdo que empezar el 5,
// solo que nombrado por la otra mitad, y por eso la lista enseña los DOS días.
export const DIAS_DE_QUINCENA = Array.from({ length: 15 }, (_, i) => i + 1);

// Los dos días en que corta la quincena de esa finca, para poder nombrarlos.
export const cortesDeQuincena = (empieza) => {
  const n = quincenaValida(empieza);
  return [n, n + 15];
};

const quincenaValida = (n) =>
  Number.isInteger(n) && n >= 1 && n <= 15 ? n : QUINCENA_POR_DEFECTO;

const ultimoDe = (y, m) => new Date(Date.UTC(y, m + 1, 0)).getUTCDate();

// Los comienzos de quincena alrededor de una fecha.
//
// Se calculan los del mes anterior, el de en medio y el siguiente, y se busca
// el último que no pase de la fecha. Así el periodo que cruza de mes —del 20 al
// 4— sale sin escribir un caso aparte, que es donde fallan estas cuentas.
//
// El segundo corte se recorta al último día del mes cuando no existe: con la
// quincena empezando el 14 o el 15, el día 29 o el 30 no está en febrero.
function comienzosDeQuincena(d, empieza) {
  const n = quincenaValida(empieza);
  const y = d.getUTCFullYear(), m = d.getUTCMonth();
  const lista = [];
  for (const off of [-1, 0, 1]) {
    const ultimo = ultimoDe(y, m + off);
    lista.push(new Date(Date.UTC(y, m + off, Math.min(n, ultimo))));
    lista.push(new Date(Date.UTC(y, m + off, Math.min(n + 15, ultimo))));
  }
  // Ordenados y sin repetidos: si el recorte de febrero hiciera coincidir los
  // dos comienzos, un periodo de cero días rompería la resta de abajo.
  return lista.sort((a, b) => a - b)
              .filter((x, i, t) => i === 0 || x.getTime() !== t[i - 1].getTime());
}

export const FRECUENCIAS = {
  semanal:    'Semanal',
  catorcenal: 'Cada 14 días',
  quincenal:  'Quincenal',
  mensual:    'Mensual',
};

// CADA 14 DÍAS NO ES LA QUINCENA, y por eso hace falta aparte.
//
// «Hay clientes a los que se les paga cada 14 días; para esos, la factura
// muestra 15 días y no 14.»
//
// La quincena parte el MES —del 1 al 15 y del 16 al fin— y por eso sus dos
// mitades tienen 15, 16 o 13 días según el mes. Catorce días son catorce
// siempre, y no se pueden anclar al calendario: el ciclo corre desde un día
// concreto y de ahí en adelante cada dos semanas.
//
// Por eso esta frecuencia necesita una fecha de arranque que las otras no
// necesitan. Sin ella no hay forma de saber cuál de las dos semanas toca.
export const ARRANQUE_POR_DEFECTO = '2026-01-05';   // un lunes

// El periodo de 14 días que contiene esa fecha, contando desde el arranque.
//
// Se cuenta en días enteros de UTC y con `Math.floor`, que redondea hacia
// abajo también en negativo: una fecha ANTERIOR al arranque cae en el ciclo
// que le toca hacia atrás, en vez de irse toda al primero.
function catorcena(d, arranque) {
  const base = utc(arranque || ARRANQUE_POR_DEFECTO);
  if (isNaN(base)) return catorcena(d, ARRANQUE_POR_DEFECTO);
  const dia = 86400000;
  const n = Math.floor((d.getTime() - base.getTime()) / (14 * dia));
  const desde = new Date(base.getTime() + n * 14 * dia);
  return { desde: ymd(desde), hasta: ymd(mas(desde, 13)) };
}

// El periodo que contiene esa fecha, según cómo se le pague a esa finca.
export function periodoDe(frecuencia, fechaDentro, empieza = EMPIEZA_POR_DEFECTO,
                          quincena = QUINCENA_POR_DEFECTO, arranque = null) {
  const d = utc(fechaDentro);
  if (isNaN(d)) return null;

  if (frecuencia === 'catorcenal') return catorcena(d, arranque);

  if (frecuencia === 'semanal') {
    // Cuántos días hay que retroceder hasta el día en que empieza la semana de
    // ESTA finca. El `+ 7` de en medio es lo que impide un resto negativo:
    // sin él, un miércoles con semana que empieza el viernes daría -2 y la
    // relación arrancaría dos días DESPUÉS del día que se está mirando.
    const inicio = Number.isInteger(empieza) && empieza >= 0 && empieza <= 6
      ? empieza : EMPIEZA_POR_DEFECTO;
    const desde = mas(d, -(((d.getUTCDay() - inicio) + 7) % 7));
    return { desde: ymd(desde), hasta: ymd(mas(desde, 6)) };
  }

  const y = d.getUTCFullYear(), m = d.getUTCMonth(), dia = d.getUTCDate();
  const fin = ultimoDelMes(d);

  if (frecuencia === 'quincenal') {
    // Del 1 al 15 y del 16 al fin de mes mientras no se diga otra cosa, que es
    // como se paga aquí. Con otro día de inicio, los dos cortes se mueven
    // juntos y el periodo puede cruzar de mes — del 20 al 4.
    const comienzos = comienzosDeQuincena(d, quincena);
    let i = 0;
    while (i + 1 < comienzos.length && comienzos[i + 1] <= d) i++;
    return { desde: ymd(comienzos[i]), hasta: ymd(mas(comienzos[i + 1], -1)) };
  }

  return { desde: ymd(new Date(Date.UTC(y, m, 1))), hasta: ymd(new Date(Date.UTC(y, m, fin))) };
}

// El periodo de al lado.
//
// El truco: para ir atrás se pide el periodo del día ANTERIOR al inicio; para
// ir adelante, el del día SIGUIENTE al final. Así sirve igual para las tres
// frecuencias sin escribir tres cuentas distintas —y sin equivocarse en la
// quincena de febrero, que es donde fallan las cuentas hechas a mano.
export function moverPeriodo(periodo, frecuencia, direccion, empieza = EMPIEZA_POR_DEFECTO,
                             quincena = QUINCENA_POR_DEFECTO, arranque = null) {
  if (!periodo) return null;
  const pivote = direccion < 0
    ? ymd(mas(utc(periodo.desde), -1))
    : ymd(mas(utc(periodo.hasta), 1));
  return periodoDe(frecuencia, pivote, empieza, quincena, arranque);
}

// Cómo se llama ese periodo, para el encabezado de la hoja.
export function nombreDelPeriodo(periodo, frecuencia, quincena = QUINCENA_POR_DEFECTO) {
  if (!periodo) return '';
  if (frecuencia === 'quincenal') {
    // Cuál de las dos es, según el día en que empieza ESTA finca. Preguntando
    // por el día 1 —como antes— la finca que corta el 5 y el 20 veía «Segunda
    // quincena» en las dos, porque ninguna de las suyas empieza el primero.
    return utc(periodo.desde).getUTCDate() === quincenaValida(quincena)
      ? 'Primera quincena' : 'Segunda quincena';
  }
  if (frecuencia === 'semanal') {
    return 'Semana del ' + DIAS[utc(periodo.desde).getUTCDay()];
  }
  // Con su nombre propio: llamarlo «quincena» sería justo la confusión que
  // vino a resolver, y son catorce días y no quince.
  if (frecuencia === 'catorcenal') return 'Catorcena';
  return 'Mes';
}
